import { useApiClient } from "../apiClients";
import { COVERAGE_ENDPOINTS } from "./endpoints";
import type { UUID } from "../../models/domain";

/**
 * État d'une offre, calculé par tc_embed_be sur les comptes seulement :
 * - `ossature_manquante` : offerte au catalogue, aucun chapitre prévu ;
 * - `a_rediger` : ossature publiée, aucun de ses chapitres importé ;
 * - `partielle` : des chapitres de l'ossature importés, pas tous jouables ;
 * - `couverte` : chaque chapitre de l'ossature est jouable.
 */
export type OfferState = "ossature_manquante" | "a_rediger" | "partielle" | "couverte";

/**
 * Une case matière × niveau × filière (`track_id` null = tronc commun) : une
 * offre du catalogue (`subject_offerings`), ou une case hors catalogue qui a
 * une ossature ou des chapitres.
 */
export interface CoverageItem {
  subject_id: UUID;
  subject_code: string;
  subject_name: string;
  grade_id: UUID;
  grade_code: string;
  grade_name: string;
  track_id: UUID | null;
  track_code: string | null;
  /** Offre du catalogue : seule base des taux. false = montrée, jamais comptée. */
  in_catalog: boolean;
  state: OfferState;
  /** Chapitres prévus par l'ossature publiée de la case. */
  ossature_chapters: number;
  /** Parmi eux, ceux qui sont importés. */
  ossature_imported: number;
  /** Parmi eux, ceux qui sont jouables. */
  ossature_playable: number;
  /** Tous les chapitres importés de la case (hors ossature compris). */
  imported: number;
  /** Parmi eux, ceux dont la dernière version a au moins un objectif avec faits. */
  playable: number;
  /** Leçons des chapitres importés (dernière version) ; absent sur un serveur ancien. */
  lessons?: number;
  /** Parmi elles, les leçons prêtes (≥ 2 objectifs, tous avec faits). */
  lessons_ready?: number;
  /** Relecture humaine des chapitres importés ; absents sur un serveur ancien. */
  to_review?: number;
  to_fix?: number;
  review_validated?: number;
}

export interface CoverageResponse {
  items: CoverageItem[];
}

/** Un chapitre d'une case : prévu par l'ossature et/ou importé. */
export interface OssatureChapterItem {
  chapter_ref: string;
  title: string;
  chapter_order: number | null;
  semester: number | null;
  subject_id: UUID;
  grade_id: UUID;
  track_id: UUID | null;
  /** false = document importé sans chapitre d'ossature correspondant. */
  in_ossature: boolean;
  document_id: UUID | null;
  document_title: string | null;
  is_active: boolean | null;
  latest_version: number | null;
  /** Objectifs de la dernière version. */
  objectives: number;
  /** Parmi eux, ceux qui ont des faits. */
  objectives_with_facts: number;
  playable: boolean;

  /*
   * Complétude PAR LEÇON (manifeste de la dernière version × objectifs
   * rattachés à leur leçon), lue sur la structure, jamais sur le texte.
   * Optionnels : un serveur pas encore à jour ne les rend pas, et l'écran ne
   * les évalue alors simplement pas.
   */
  /** Leçons du manifeste. */
  lessons?: number;
  /** Prêtes : ≥ 2 objectifs, tous avec faits. */
  lessons_ready?: number;
  /** Faibles : ≥ 1 objectif, mais moins de 2 ou certains sans faits. */
  lessons_weak?: number;
  /** Vides : aucun objectif. */
  lessons_empty?: number;
  /** Relecture humaine ; null = pas importé ; absent sur un serveur ancien. */
  review_status?: "non_requise" | "a_relire" | "validee" | "a_corriger" | null;
}

export interface OssatureChaptersParams {
  program_id: UUID;
  subject_id?: UUID;
  grade_id?: UUID;
  /** uuid de filière, ou `common` pour le tronc commun seul. */
  track_id?: UUID | "common";
}

export const useCoverageApi = () => {
  const apiClient = useApiClient("embed");

  const getCoverage = (programId: UUID) =>
    apiClient.get<CoverageResponse>(COVERAGE_ENDPOINTS.BASE, {
      params: { program_id: programId },
    });

  const listOssatureChapters = (params: OssatureChaptersParams) =>
    apiClient.get<{ items: OssatureChapterItem[] }>(COVERAGE_ENDPOINTS.OSSATURE_CHAPTERS, { params });

  return { getCoverage, listOssatureChapters };
};
